import { Injectable } from '@angular/core';
import { Adapter } from '@core/adapter';
import { OrganisationProfile } from './organisation-profile';
import { Department, DepartmentAdapter } from './department.model';
import { JointCommittee } from './joint-committee.model';
import { OrganisationSubscription, OrganisationSubscriptionAdapter } from './organisation-subscription';
import { OrganisationJointCommittee, OrganisationJointCommitteeAdapter } from './organisation-joint-committee.model';
import { FocalPoint } from '@shared/components/focalpoint-picker/focalpoint-picker.component';
import { BranchService } from '@core/services/branch.service';

export interface Organisation {
  id: string;
  name: string;
  vatNumber: string;
  country:string;
  image: string;
  focalPoint:FocalPoint|null;
  profiles: OrganisationProfile[];
  member: boolean;
  verified:boolean;
  admin: boolean;
  manager:boolean;
  active: boolean;
  createdAt?: Date;
  adminName: string;
  departments: Department[];
  committees: OrganisationJointCommittee[];
  isPremium:boolean;
  amountOfActiveEmployees:number;
  amountOfFullTimeEquivalentEmployees:number;
  amountOfMembers:number;
  amountOfAdmins:number;
  referral?:string;
  currentSubscription?:OrganisationSubscription;
  currentSubscriptionAutomaticallyGranted?:boolean;
  nextSubscription?:OrganisationSubscription;
  nextSubscriptionAutomaticallyGranted?:boolean;
  requestCount:number;
  trialSubscriptionAlreadyRedeemed?:boolean;
  startTrainingRightYear?:number;
  icalUrl:string;

  //modules
  costPriceModule:boolean;
  allowsCourseRequest:boolean;
  allowsCustomCourseRequest:boolean;
  grantModuleEnabled:boolean;
  presenceModuleEnabled:boolean;
  attachmentModuleEnabled:boolean;
  budgetModuleEnabled:boolean;
  isObligatedDateForRequest:boolean;
  allowExternalConnection:boolean;
  certificationModuleEnabled:boolean;
  qualificationModuleEnabled:boolean;

  //mail
  mailNotificationSettings:{
    isCertificateExpiringEnabled:boolean;
    isFlaSyncMailEnabled:boolean;
    certificateExpirationTerms:number;
    isEnrollNotificationEnabled:boolean;
    isRequestCourseNotificationEnabled:boolean;
    isNightlyEmployeeCourseNotificationEnabled:boolean;
    isFtpImportNotificationsEnabled:boolean;
    technicalContactEmails:string;
    isCourseNotificationEnabled:boolean;
    amountOfDaysForCourseNotification:number;
    // The qualification follow-up: what is running out, what is due, and what was sat without an
    // outcome. The expiry and deadline days look ahead; the outcome days look back.
    isCertificationExpiryNotificationEnabled:boolean;
    certificationExpiryNotificationDays:number;
    isCertificationDeadlineNotificationEnabled:boolean;
    certificationDeadlineNotificationDays:number;
    isQualificationExpiryNotificationEnabled:boolean;
    qualificationExpiryNotificationDays:number;
    isQualificationDeadlineNotificationEnabled:boolean;
    qualificationDeadlineNotificationDays:number;
    isQualificationOutcomeNotificationEnabled:boolean;
    qualificationOutcomeNotificationDays:number;
  }

  //featurelimits
  features:OrganisationFeature[];
  maxEmployeesReached?:boolean;
  hasFLAAccess?:boolean;
  canInviteMembers?:boolean;
  hasFpsAccess?:boolean;
  hasReadOnlyAccess?:boolean;
  hasPresenceModuleAccess?:boolean;
  hasAdvancedPermissionAccess?:boolean;
  hasAttachmentModuleAccess?:boolean;

  //role
  role:RoleType
  permissions:{
    canManageAdministrators:boolean;
    canManageWages:boolean;
    canManageAttachments:boolean;
    // The legacy certificate module; the four below are the qualification catalogue's own rights.
    canManageCertificates:boolean;
    canAddColleaguesToRequest:boolean;
    canReadCertifications?:boolean;
    canManageCertifications?:boolean;
    canReadQualifications?:boolean;
    canManageQualifications?:boolean;
    canAccessTrainingRequests?:boolean;
  }

  //AI
  freeAiQueriesRemaining?: number;
  hasAiFeature?: boolean;
}

/**
 * Everything an employment hangs on: at least one department and one joint committee. What the
 * onboarding flow leaves behind, and what it checks its own save against.
 */
export function isOrganisationSetupComplete(organisation: Organisation): boolean {
  return organisation.departments.length > 0 && organisation.committees.length > 0;
}

/**
 * A workspace the onboarding flow still has to walk an admin through: a subscription is running, so
 * the organisation is in use, but a department or a joint committee is still missing. Both are
 * needed — an employment hangs on the two of them — so either one missing is unfinished.
 */
export function needsOrganisationSetup(organisation: Organisation): boolean {
  // `isPremium` is the API's `activeSubscription`, but it is forced off on a vanity domain, so the
  // subscription itself is read as well: whether the flow belongs there is the caller's call.
  const subscribed = organisation.isPremium || (!!organisation.currentSubscription && !organisation.currentSubscription.expired);
  return subscribed && !isOrganisationSetupComplete(organisation);
}

/**
 * An organisation that has never been on a subscription: nothing running, nothing lined up, and its
 * trial still unspent. An expired subscription counts as having had one, so it is read as presence
 * and not on `expired` the way `needsOrganisationSetup` does.
 */
export function hasNeverBeenSubscribed(organisation: Organisation): boolean {
  return !organisation.isPremium
    && !organisation.currentSubscription
    && !organisation.nextSubscription
    && !organisation.trialSubscriptionAlreadyRedeemed;
}

/**
 * Whether the onboarding flow, once it runs, still owes this organisation a company size, the licence
 * agreement and its trial: a real company (VAT number) that never chose a subscription and never
 * spent its trial. The Free subscription every new organisation is handed automatically does not
 * count as a choice — read as one, it kept the size, the agreement and the trial out of the setup of
 * every organisation made from the "new organisation" page. Nor does its automatic renewal, which the
 * backend books as the next subscription right away.
 *
 * Deliberately not what decides whether the flow runs at all (`needsOrganisationOnboarding`): an
 * organisation set up on that Free plan is not sent back into onboarding for it.
 */
export function owesFirstSubscription(organisation: Organisation): boolean {
  const automatic = !!organisation.currentSubscriptionAutomaticallyGranted;
  return !!organisation.vatNumber
    && (!organisation.isPremium || automatic)
    && (!organisation.currentSubscription || automatic)
    && (!organisation.nextSubscription || !!organisation.nextSubscriptionAutomaticallyGranted)
    && !organisation.trialSubscriptionAlreadyRedeemed;
}

/**
 * A real company (it carries a VAT number) that was never subscribed: it exists — members were
 * imported into it — but it was never onboarded, so the flow still owes it a company size and a
 * trial before the workspace is used.
 */
export function needsSubscriptionOnboarding(organisation: Organisation): boolean {
  return !!organisation.vatNumber && hasNeverBeenSubscribed(organisation);
}

/** Either reason the onboarding flow has to run inside a workspace. */
export function needsOrganisationOnboarding(organisation: Organisation): boolean {
  return needsOrganisationSetup(organisation) || needsSubscriptionOnboarding(organisation);
}

@Injectable({
  providedIn: 'root'
})

export class OrganisationAdapter implements Adapter<Organisation> {
  constructor(
    private departmentAdapter:DepartmentAdapter,
    private commiteeAdapter:OrganisationJointCommitteeAdapter,
    private subAdapter:OrganisationSubscriptionAdapter,
    private branchService:BranchService
  ){}
  adapt(item: any): Organisation {
    const mailNotificationSettings = item.mailNotificationSettings;
    if(!mailNotificationSettings.amountOfDaysForCourseNotification){
        mailNotificationSettings.amountOfDaysForCourseNotification = 1;
    }
    return {
      id: item.id??'',
      name: item.name,
      country:item.isoCountry	??'',
      vatNumber:item.vatNumber,
      active: item.active ?? false,
      verified:item.isVerifiedMember??false,
      admin: item.isAdmin??false,
      member: item.isMember??false,
      adminName: item.adminName??'',
      manager:item.isManager??false,
      image: item.logo? (item.logo.fileUrl?(item.logo.fileUrl +'?filter=thumb&width=200&height=200&mode=outbound'):'' ) :'',
      profiles:[],
      committees: (item.jointCommittees??[]).map((c:any) => this.commiteeAdapter.adapt(c)),
      departments: [],
      createdAt:item.createdAt,
      // No premium features are allowed on vanity (branded partner) URLs, so treat the org as non-premium there.
      isPremium: (item.activeSubscription ?? false) && !this.branchService.vanityMode,
      referral: item.currentSubscription && item.currentSubscription.referral? item.currentSubscription.referral.id :'',
      amountOfActiveEmployees:item.amountOfActiveEmployees??0,
      amountOfFullTimeEquivalentEmployees:item.amountOfFullTimeEquivalentEmployees??0,
      amountOfMembers: item.amountOfMembers??0,
      amountOfAdmins: (item.admins?? []).length,
      currentSubscription : item.currentSubscription? this.subAdapter.adapt(item.currentSubscription) : null,
      currentSubscriptionAutomaticallyGranted: item.currentSubscription?.isAutomaticallyGranted ?? false,
      nextSubscription : item.nextSubscription? this.subAdapter.adapt(item.nextSubscription) : null,
      nextSubscriptionAutomaticallyGranted: item.nextSubscription?.isAutomaticallyGranted ?? false,
      costPriceModule: (item.costModuleEnabled??true),
      allowsCourseRequest: (item.courseEnrolmentModuleEnabled??false),
      allowsCustomCourseRequest: (item.requestCustomCourseModuleEnabled??false),
      requestCount:item.requestCount??0,
      startTrainingRightYear:item.startTrainingRightYear??2024,
      trialSubscriptionAlreadyRedeemed : item.trialSubscriptionAlreadyRedeemed ?? false,
      isObligatedDateForRequest: item.isObligatedDateForRequest ?? false,
      icalUrl: item.icalUrl??'',
      mailNotificationSettings : mailNotificationSettings,
      allowExternalConnection: !item.activeSubscription || (item.allowExternalConnection??false),
      permissions:item.permissions,
      focalPoint: {x:0,y:0},

      //features
      maxEmployeesReached: item.features ? item.features.maxEmployeesReached : false,
      hasFLAAccess: item.isManager?false :  (item.features ? item.features.fla_integration.hasAccess : false),
      canInviteMembers: item.features ? item.features.employee_training_access.enabled : false,
      hasFpsAccess: item.isManager?false : (item.features? item.features.fps_employment_integration.hasAccess : false),
      grantModuleEnabled: item.grantModuleEnabled ?? false,
      hasReadOnlyAccess: (item.features? item.features.advanced_administrator_access_roles.hasAccess : false),
      hasAdvancedPermissionAccess: (item.features? item.features.advanced_administrator_access_roles.hasAccess : false),
      presenceModuleEnabled:(item.features? item.features.presence_module.enabled:false), 
      hasPresenceModuleAccess: (item.features? item.features.presence_module.hasAccess : false),    
      attachmentModuleEnabled: (item.features? item.features.attachment_module.enabled : false),
      budgetModuleEnabled: (item.features? item.features.budget_module.enabled : false),
      certificationModuleEnabled: (item.features? item.features.certification?.enabled : false),
      qualificationModuleEnabled: (item.features? item.features.qualification?.enabled ?? false : false),
      hasAttachmentModuleAccess: (item.features? item.features.attachment_module.hasAccess : false),
      freeAiQueriesRemaining: item.freeAiQueriesRemaining ?? 0,
      hasAiFeature: (item.features? item.features.ai_usage.enabled : false),
            
      features:  Object.keys(item.features).map((key:any) => {
          const f = item.features[key];
          return { 
            name:key,
            currentUsage: f.currentUsage ?? 0,
            enabled: f.enabled ?? false,
            hasAccess: f.hasAccess ?? false, 
            limit : f.limit ?? 0, 
            limitReached: !f.canAdd, 
            remaining: f.remaining ?? 0,
            unlimited: f.unlimited
          } as OrganisationFeature
      }),
      role: setRoleType(item)
    } as Organisation
  }
  
  adaptForLookup(item: any): Organisation {
    return {
      id: item.id??'',
      name: item.name,
      country:item.isoCountry	??'BE',
      vatNumber:item.vatNumber,
      active: true,
      verified:item.isVerifiedMember??false,
      member: item.isMember??false,
      admin: item.isAdmin??false,
      manager:item.isManager??false,
      adminName: item.adminName??'',
      image: item.logo? (item.logo.fileUrl?(item.logo.fileUrl +'?filter=thumb&width=200&height=200&mode=outbound'):'' ) :'',
      profiles:[],
      committees: (item.jointCommittees??[]).map((c:any) => {return { id: c.id, name: 'PC '+ c.number, description: c.description} as JointCommittee}),
      departments: [],
      createdAt:item.createdAt,
      isPremium: (item.activeSubscription ?? false) && !this.branchService.vanityMode,
      currentSubscriptionAutomaticallyGranted: item.currentSubscription?.isAutomaticallyGranted ?? false,
      subscriptions: (item.subscriptions??[]).map((s:any) => this.subAdapter.adapt(s)),
      amountOfActiveEmployees:item.amountOfActiveEmployees??0,
      amountOfFullTimeEquivalentEmployees:item.amountOfFullTimeEquivalentEmployees??0,
      amountOfMembers: item.amountOfMembers??0,
      amountOfAdmins: (item.admins?? []).length,
      costPriceModule:item.costModuleEnabled??true,      
      allowsCourseRequest:item.courseEnrolmentModuleEnabled??false,
      // The two modules are independent: an org can allow custom course requests without opening up
      // its course catalogue for self-enrolment. Reading one as a precondition for the other hid
      // those orgs from the request flow.
      allowsCustomCourseRequest:item.requestCustomCourseModuleEnabled??false,
      presenceModuleEnabled:item.presenceModuleEnabled??false,     
      isObligatedDateForRequest: item.isObligatedDateForRequest ?? false,
      requestCount:item.requestCount??0,
      grantModuleEnabled: item.grantModuleEnabled ?? false,
      attachmentModuleEnabled:false,
      budgetModuleEnabled:false,
      certificationModuleEnabled:true,
      qualificationModuleEnabled:true,
      mailNotificationSettings : item.mailNotificationSettings,
      permissions:item.permissions??{},
      icalUrl:'',
      role: setRoleType(item),
      allowExternalConnection: !item.activeSubscription || (item.allowExternalConnection??false),
      features:[],
      focalPoint:null,
    } as Organisation

  }

  
}

export function setRoleType(item:any){
    
    if(item.isAdmin) return RoleType.ADMIN;
    if(item.isManager	&& !item.isReadOnly) return RoleType.MANAGER;
    if(item.isManager	&& item.isReadOnly) return RoleType.MANAGER_READONLY;
    if(item.isAuditor) return RoleType.AUDITOR;
    return RoleType.MEMBER;
}


export enum RoleType{
  MEMBER = 'MEMBER',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MANAGER_READONLY = 'MANAGER_READONLY',
  AUDITOR = 'AUDITOR'
}

export interface OrganisationFeature{
  name:string;
  enabled:boolean;
  limitReached:boolean;
  unlimited:boolean;
  currentUsage:number;
  limit:number;
  remaining:number;

  hasAccess:boolean;
}